import { useMemo, useRef, useState } from "react";

import { canUsePermission } from "../../../lib/auth";
import { isHttpClientError } from "../../../lib/http/errors";
import { useCurrentAuthUser } from "../../auth";
import { buildMemberFeeMarkupPreview, calculateMemberFeeAdjustmentSummary, useMemberFeeAdjustmentQuery, useUpdateFeeAdjustmentSalesAmount } from "../../fee-adjustment";
import type { FeeAdjustmentRecord } from "../../fee-adjustment";

export type MemberFeeAdjustmentDetailParams = { projectId: string; rosterId: string; memberId: string; customerQuoteId?: string };

/** 后端统一校验归属，读取失败不转成零调整。 */
export function useMemberFeeAdjustmentDetail({ projectId, rosterId, memberId, customerQuoteId }: MemberFeeAdjustmentDetailParams) {
  const userQuery = useCurrentAuthUser();
  const canAccess = canUsePermission(userQuery.data, "fee_adjustment.request:access");
  const canUpdate = canUsePermission(userQuery.data, "fee_adjustment.request:update");
  const validParams = Boolean(projectId.trim() && rosterId.trim() && memberId.trim());
  const recordsQuery = useMemberFeeAdjustmentQuery(projectId, rosterId, memberId, customerQuoteId, validParams && canAccess);
  const detail = recordsQuery.data;
  const responseMatches = Boolean(detail && detail.project_id === projectId && detail.roster_id === rosterId
    && detail.member.id === memberId && (!customerQuoteId || detail.customer_quote_id === customerQuoteId)
    && detail.records.every((record) => record.roster_id === rosterId && record.order_id === detail.order_id
      && record.customer_quote_id === detail.customer_quote_id && record.members.some((item) => item.roster_member_id === memberId)));
  const records = useMemo(() => detail?.records ?? [], [detail?.records]);
  let error = "";
  if (!validParams) error = "成员费用调整地址不完整";
  else if (!userQuery.isPending && !canAccess) error = "当前账号没有查看费用调整记录的权限";
  else if (recordsQuery.isError) error = isHttpClientError(recordsQuery.error) && recordsQuery.error.statusCode === 404
    ? "成员与当前项目名单或报价版本不匹配" : "费用调整记录加载失败，请重试";
  else if (recordsQuery.isSuccess && !responseMatches) error = "费用调整记录与当前名单报价不匹配";
  const loading = !error && (userQuery.isPending || recordsQuery.isPending);
  const ready = !loading && !error && responseMatches;
  const contextRejected = recordsQuery.isError && isHttpClientError(recordsQuery.error)
    && [401, 403, 404].includes(recordsQuery.error.statusCode ?? 0);
  const retainLoadedRecords = canAccess && responseMatches && !contextRejected;
  const summary = useMemo(() => ready ? calculateMemberFeeAdjustmentSummary(detail?.member.base_receivable_amount ?? null, memberId, records) : null, [ready, detail?.member.base_receivable_amount, memberId, records]);
  return {
    member: retainLoadedRecords && detail ? { ...detail.member, classGroup: detail.member.class_group } : undefined,
    records: retainLoadedRecords ? records : [], summary, loading, error,
    canUpdate: canAccess && canUpdate, recordsCurrent: !recordsQuery.isError, refetchRecords: recordsQuery.refetch,
    retry() { void recordsQuery.refetch(); },
  };
}

/** 每笔申请共享保存互斥；失败保留草稿，冲突只刷新而不自动覆盖。 */
export function useMemberFeeAdjustmentRequest(record: FeeAdjustmentRecord, memberId: string, canUpdate: boolean, refetchRecords: () => Promise<unknown>) {
  const mutation = useUpdateFeeAdjustmentSalesAmount(record.roster_id, record.request_id);
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState<{ error: boolean; text: string } | null>(null);
  const [batch, setBatch] = useState<{ lineId: string; amount: string; revision: number } | null>(null);
  const member = record.members.find((item) => item.roster_member_id === memberId);
  const allowed = (lineId: string) => Boolean(canUpdate && record.status === "processed" && member?.is_payer && record.lines.find((line) => line.id === lineId)?.can_member_markup === true);
  const batchPreview = batch ? buildMemberFeeMarkupPreview(record, batch.lineId, memberId, batch.amount) : null;

  /** 保存本行总额及全部出钱人的分摊，保留其他行草稿和冲突核对机会。 */
  async function save(lineId: string, amount: string, revision: number) {
    const preview = buildMemberFeeMarkupPreview(record, lineId, memberId, amount);
    if (!allowed(lineId) || !preview || savingRef.current || mutation.isPending) return;
    if (revision !== record.revision) { setFeedback({ error: true, text: "记录已更新，请核对后重新应用" }); setBatch(null); return; }
    savingRef.current = true;
    setSaving(true);
    setFeedback(null);
    try {
      await mutation.mutateAsync({ expected_revision: revision, line_id: lineId, total_amount: preview.amount });
      setDrafts((current) => { const next = { ...current }; delete next[lineId]; return next; });
      setBatch(null);
      setFeedback({ error: false, text: "加价金额已保存" });
    } catch (error) {
      if (isHttpClientError(error) && error.statusCode === 409) {
        setBatch(null);
        try {
          const result = await refetchRecords();
          const refreshFailed = Boolean(result && typeof result === "object" && "isError" in result && result.isError);
          setFeedback({ error: true, text: refreshFailed ? "记录已更新，最新记录加载失败；输入已保留，请重试加载" : "记录已更新，请核对后重新应用" });
        } catch {
          setFeedback({ error: true, text: "记录已更新，最新记录加载失败；输入已保留，请重试加载" });
        }
      } else setFeedback({ error: true, text: "加价金额保存失败，请重试" });
    } finally { savingRef.current = false; setSaving(false); }
  }
  return {
    drafts, feedback, saving: saving || mutation.isPending, batchPreview,
    setAmount(lineId: string, amount: string) { setDrafts((current) => ({ ...current, [lineId]: amount })); },
    previewBatch(lineId: string, amount: string) {
      if (allowed(lineId) && !savingRef.current && buildMemberFeeMarkupPreview(record, lineId, memberId, amount)) setBatch({ lineId, amount, revision: record.revision });
    },
    cancelBatch() { if (!savingRef.current) setBatch(null); },
    confirmBatch() { if (batch) return save(batch.lineId, batch.amount, batch.revision); },
  };
}
